import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { BaseComponent } from '../../core';
import {
  BreakpointService,
  CustomerService,
  DialogService,
  ErrorHandlerServiceService,
  RewardService,
} from '../../services';
import { APP_MODE, BusinessObject, Customer } from '../../models';
import {
  LibCoreLayoutModule,
  SuccessCheckAnimationDialogComponent,
  SuccessCheckAnimationDialogComponentData,
} from '../core-layout';
import { DateFormatterPipe } from '../../pipes';
import { MatButtonModule } from '@angular/material/button';
import { CustomerTypeFormatterPipe } from '../../pipes/customer-type-formatter.pipe';
import { CustomerFormComponent } from '../customer-form/customer-form.component';

export interface CustomerFormDialogComponentData {
  mode: APP_MODE;
  displayFullContactData: boolean;
  businessObject: BusinessObject;
  customer?: Customer;
}

@Component({
  selector: 'lib-customer-dialog',
  templateUrl: './customer-form-dialog.component.html',
  styleUrls: ['./customer-form-dialog.component.scss'],
  standalone: true,
  imports: [
    LibCoreLayoutModule,
    TranslateModule,
    ReactiveFormsModule,
    CustomerFormComponent,
    // material design
    MatButtonModule,
    MatDialogModule,
  ],
})
export class CustomerFormDialogComponent extends BaseComponent {
  public mode: APP_MODE;
  public displayFullContactData: boolean;
  public customerToEdit: Customer;
  public businessObject: BusinessObject;

  public customerFormControl = new FormControl<Customer>(null, [
    Validators.required,
  ]);

  public isLoading: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CustomerFormDialogComponentData,
    public breakpointService: BreakpointService,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private dialogService: DialogService,
    private customerService: CustomerService,
    private rewardService: RewardService,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<CustomerFormDialogComponent>,
    private customerTypeFormatterPipe: CustomerTypeFormatterPipe,
    private dateFormatterPipe: DateFormatterPipe
  ) {
    super();

    this.mode = this.data?.mode;
    this.displayFullContactData = this.data?.displayFullContactData;
    this.businessObject = this.data?.businessObject;
    this.customerToEdit = this.data?.customer;

    this.customerFormControl.setValue(this.customerToEdit);
  }

  public isAbleToEditLoyalPoint(): boolean {
    return this.mode === APP_MODE.ADMIN;
  }

  public generateSubtitle(): string {
    if (!this.customerToEdit?.id) {
      return null;
    }

    const typeLabel = this.translateService.instant(
      'customer-form-dialog.field.type'
    );
    const typeValue = this.customerTypeFormatterPipe.transform(
      this.customerToEdit?.type?.name
    );

    const numberOfVisitLabel = this.translateService.instant(
      'customer-form-dialog.field.number_of_visit'
    );
    const numberOfVisitValue = this.customerToEdit?.numberOfVisits || 0;

    const registeredSinceLabel = this.translateService.instant(
      'customer-form-dialog.field.registered_since'
    );
    const registeredSinceValue = this.dateFormatterPipe.transform(
      this.customerToEdit?.registeredAtAsDate
    );

    const lastCheckinLabel = this.translateService.instant(
      'customer-form-dialog.field.last_checkin'
    );
    const lastCheckinValue = this.dateFormatterPipe.transform(
      this.customerToEdit?.lastCheckInAsDate
    );

    return `${typeLabel}: ${typeValue}, ${numberOfVisitLabel}: ${numberOfVisitValue}, ${registeredSinceLabel}: ${registeredSinceValue}, ${lastCheckinLabel}: ${lastCheckinValue}`;
  }

  public isAbleToSubmit(): boolean {
    return this.customerFormControl.valid && !this.isLoading;
  }

  public async submit() {
    if (!this.isAbleToSubmit()) {
      return;
    }

    this.isLoading = true;

    try {
      const dataToSubmit: Customer = this.customerFormControl.value;

      let updatedOrCreatedCustomer: Customer;

      if (this.customerToEdit?.id) {
        updatedOrCreatedCustomer = await this.updateCustomer(dataToSubmit);

        this.dialogService.openDialog(
          SuccessCheckAnimationDialogComponent,
          this.dialogService.createSmallDialogConfig({
            text: 'customer-form-dialog.text.edit_customer_success',
          } as SuccessCheckAnimationDialogComponentData)
        );
      } else {
        updatedOrCreatedCustomer = await this.createCustomer(
          this.businessObject?.id,
          dataToSubmit
        );

        this.dialogService.openDialog(
          SuccessCheckAnimationDialogComponent,
          this.dialogService.createSmallDialogConfig({
            text: 'customer-form-dialog.text.create_customer_success',
          } as SuccessCheckAnimationDialogComponentData)
        );
      }

      if (updatedOrCreatedCustomer) {
        this.dialogRef.close(updatedOrCreatedCustomer);
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  private async updateCustomer(customer: Customer): Promise<Customer> {
    // update customer
    await this.customerService.updateCustomerById(customer);

    if (customer.pointUpdate && Number.isInteger(customer.pointUpdate)) {
      // update customer available point balance
      await this.rewardService.updateBonusPointBalance(
        customer.id,
        customer.pointUpdate
      );

      customer.availableBonusPoints += customer.pointUpdate;
      customer.totalBonusPoints += customer.pointUpdate;
    }

    return customer;
  }

  private createCustomer(boId: number, customer: Customer): Promise<Customer> {
    return this.customerService.createNewCustomerForBusinessObject(
      boId,
      customer
    );
  }
}
